import React, { useState } from 'react';
import cx from 'classnames';

import { useMedia, DESKTOP_MENU_QUERY } from '../../../../utils/useMedia';

import * as cs from './AsideColumnLayoutSlot.module.css';

const UNI_SENTINEL_ATTR = 'data-sentinel';
const EDGE_SLOT_PADDING = 48;
const ADDITIONAL_PADDING = 90;

const getRootMargin = (
  position: string,
  slotHeight: number,
  winHeight: number,
) => {
  let centerMargin = 0;

  switch (position) {
    case 'top':
      return `${-(EDGE_SLOT_PADDING + ADDITIONAL_PADDING / 2)}px 0px ${-(
        winHeight -
        slotHeight +
        ADDITIONAL_PADDING / 2
      )}px 0px`;
    case 'center':
      centerMargin = winHeight / 2 - slotHeight / 2 + ADDITIONAL_PADDING;
      return `${-centerMargin}px 0px ${-centerMargin}px 0px`;
    case 'bottom':
      return `${-(winHeight - slotHeight + ADDITIONAL_PADDING / 2)}px 0px ${-(
        EDGE_SLOT_PADDING +
        ADDITIONAL_PADDING / 2
      )}px 0px`;
  }
};

interface SentinelMeta {
  isIntersecting: boolean;
  primaryColor: string;
  secondaryColor: string;
  hidden: boolean;
  ts: number;
}

const pickMeta = (inters: Record<string, SentinelMeta>) => {
  const res = {
    hasIntersecting: false,
    primaryColor: '',
    hidden: false,
    secondaryColor: '',
  };
  let pTs = -Infinity;
  let sTs = -Infinity;

  for (const key in inters) {
    const data = inters[key];
    res.hasIntersecting = data.isIntersecting || res.hasIntersecting;

    if (data.isIntersecting && data.primaryColor && data.ts > pTs) {
      res.primaryColor = data.primaryColor;
      pTs = data.ts;
    }

    if (data.isIntersecting && data.secondaryColor && data.ts > sTs) {
      res.secondaryColor = data.secondaryColor;
      sTs = data.ts;
    }

    if (data.isIntersecting && data.hidden) {
      res.hidden = true;
    }
  }

  return res;
};

interface Props {
  children: React.ReactNode;
  position: 'top' | 'center' | 'bottom';
  forceUseTheme?: boolean;
  desktopOnly?: boolean;
  hideOnTablet?: boolean;
}

export const AsideColumnLayoutSlot = ({
  children,
  position,
  forceUseTheme,
  desktopOnly,
  hideOnTablet,
}: Props) => {
  const [windowHeight, setWindowHeight] = useState(
    (typeof window !== 'undefined' && window.innerHeight) || 0,
  );
  const [inters, setInters] = React.useState<Record<string, SentinelMeta>>({});
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMedia(DESKTOP_MENU_QUERY);
  const meta = pickMeta(inters);

  const style = React.useMemo(
    () =>
      ({
        '--color-theme-logo': meta.primaryColor,
        '--color-theme-button': meta.secondaryColor,
        '--color-theme-navigation': meta.primaryColor,
      }) as React.CSSProperties,
    [meta.primaryColor, meta.secondaryColor],
  );

  React.useEffect(() => {
    if (
      !isDesktop ||
      !wrapperRef.current ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    const onIntersect: IntersectionObserverCallback = (entries) => {
      const inters: Record<string, SentinelMeta> = {};
      for (const entry of entries) {
        const sentinel = entry.target as HTMLElement;
        const sId = sentinel?.dataset.sentinelId as string;

        inters[sId] = {
          isIntersecting: entry.isIntersecting,
          primaryColor: sentinel?.dataset.primaryColor || '',
          secondaryColor: sentinel?.dataset.secondaryColor || '',
          hidden: sentinel?.dataset.hidden === 'true',
          ts: Date.now(),
        };
      }

      setInters((val) => ({ ...val, ...inters }));
    };

    const nodes = document.querySelectorAll(`[${UNI_SENTINEL_ATTR}]`);

    if (!nodes.length) return;

    const slotHeight = wrapperRef.current.clientHeight;

    const observer = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: getRootMargin(position, slotHeight, windowHeight),
      threshold: 0,
    });

    for (const node of nodes) {
      observer.observe(node);
    }

    return () => {
      for (const node of nodes) {
        observer?.unobserve(node);
      }
      observer?.disconnect();
    };
  }, [position, isDesktop, children, windowHeight]);

  React.useEffect(() => {
    function resizeListener() {
      setWindowHeight(window.innerHeight);
    }
    window.addEventListener('resize', resizeListener);
    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  return (
    <div
      className={cx(cs[`position_${position}`], cs.root, {
        [cs.desktopOnly]: desktopOnly,
        [cs.hideOnTablet]: hideOnTablet,
        [cs.hidden]: meta.hidden,
      })}
      data-theme-reset={
        forceUseTheme || !isDesktop ? false : !meta.hasIntersecting
      }
      ref={wrapperRef}
      style={style}
    >
      {children}
    </div>
  );
};
